<template>
    <div class="bar">
        <div class="fetch" v-bind:class="[{'is-loading': isLoading}]" v-if="hasResults">
            <div class="bar-chart">
                <canvas :id="chartid" v-bind:height="chartHeight"></canvas>
            </div>
            
            <div v-if="showLegend">
                <ol v-if="hasResults" class="l-clearfix bar-legend" :class="[legendColor]">
                    <li class="l-clearfix bar-legend-series" v-for="(amount, index) in barChartAmounts">
                      <router-link v-if="chartLegendLink" :to="{name: chartLegendLink, params: {id: barChartIds[index]}}" :class="'bar-legend-series-item bar-legend-series-link'">
                        <span class="bar-legend-title s-icon">
                          <i>
                            {{index + 1}}{{ index === 0 ? 'st' : '' }}{{ index === 1 ? 'nd' : '' }}{{ index === 2 ? 'rd' : '' }}{{ index > 2 ? 'th' : '' }}
                          </i>
                          {{ barChartLabels[index] }}
                        </span>
                        <span class="bar-legend-amount">{{ prefix }}{{ Number(amount).toLocaleString('en-US') }}</span>
                      </router-link>
                      <div v-else>
                        <span class="bar-legend-series-item">
                          <span class="bar-legend-title">{{ barChartLabels[index] }}</span>
                          <span class="bar-legend-amount">{{ prefix }}{{ Number(amount).toLocaleString('en-US') }}</span>
                        </span>
                      </div>
                    </li>
                    <li v-if="noDataAvailable">
                      <span class="bar-legend-title">{{ noDataAvailable.title }}</span>
                      <span class="bar-legend-amount">{{ prefix }}{{ Number(noDataAvailable.amount).toLocaleString('en-US') }}</span>
                    </li>
                </ol>
            </div>
        </div>
        <div v-else class="bar-message" v-bind:class="[{'is-loading': isLoading}]">
            <p>No results that match those filters.</p>
        </div>
        <div class="bar-loader" v-bind:class="[{'is-loading': isLoading}]"><LoaderBars></LoaderBars></div>
    </div>
</template>

<script>
import Chart from 'chart.js'
import LoaderBars from '@/components/LoaderBars'

export default {
  name: 'BarChart',

  components: {
    'LoaderBars': LoaderBars
  },

  props: {
    'barColor': {
      type: String,
      required: false
    },
    'chartData': {
      type: Array,
      required: true
    },
    'chartHeight': {
      type: Number,
      required: false,
      default: 180
    },
    'chartType': {
      type: String,
      required: false,
      default: 'horizontalBar'
    },
    'labelPrefix': {
      type: String,
      required: false
    },
    'legendColor': {
      type: String,
      required: false
    },
    'legendLink': {
      type: String,
      required: false
    },
    'showLegend': {
      type: Boolean,
      required: false,
      default: true
    },
    'stacked': {
      type: Boolean,
      required: false,
      default: false
    },
    'xMaxAxesRotation': {
      type: Number,
      required: false,
      default: 90
    },
    'xMinAxesRotation': {
      type: Number,
      required: false,
      default: 0
    }
  },

  data: function () {
    return {
      chartid: 'bar' + this._uid
    }
  },

  computed: {
    isLoading () {
      if (Array.isArray(this.chartData)) {
        // == done loading and no results.
        if (this.chartData[0] === -1) {
          return 0
        }
      }

      return (this.barChartAmounts.length === 0) ? 1 : 0
    },

    hasResults () {
      if (Array.isArray(this.chartData)) {
        return (this.chartData[0] === -1) ? 0 : 1
      }

      return 0
    },

    noDataAvailable () {
      if (this.hasResults) {
        return this.chartData.filter(function (series) {
          return series.title === 'No data available'
        })[0]
      }

      return []
    },

    barChartAmounts () {
      if (this.hasResults) {
        return this.chartDataWithoutNullValues.map(function (data) { return data.amount })
      }

      return []
    },

    barChartLabels () {
      if (this.hasResults) {
        return this.chartDataWithoutNullValues.map(function (data) { return data.title })
      }

      return []
    },

    barChartIds () {
      if (this.hasResults) {
        return this.chartDataWithoutNullValues.map(function (data) {
          if (typeof data.slug !== 'undefined') {
            return data.slug
          }

          return data.id
        })
      }

      return []
    },

    chartBarColor () {
      return this.barColor
    },

    chartLegendLink () {
      return this.legendLink
    },

    chartDataWithoutNullValues () {
      if (this.hasResults) {
        return this.chartData.filter(function (series) {
          return series.title !== 'No data available'
        })
      }

      return []
    },

    prefix () {
      return this.labelPrefix ? this.labelPrefix : ' '
    }
  },

  watch: {
    chartData: function () {
      if (typeof this.theChart === 'object') {
        this.theChart.destroy()
      }

      let vm = this
      let data = {
        labels: vm.barChartLabels,
        datasets: [{
          backgroundColor: vm.chartBarColor,
          data: vm.barChartAmounts
        }]
      }

      if (vm.barChartAmounts.length > 0) {
        vm.theChart = new Chart(document.getElementById(vm.chartid), {
          type: vm.chartType,
          data: data,
          options: {
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              xAxes: [{
                stacked: vm.stacked,
                ticks: {
                  maxRotation: vm.xMaxAxesRotation,
                  minRotation: vm.xMinAxesRotation,
                  beginAtZero: true,
                  autoSkip: false,
                  callback: function (value, index, values) {
                    if (vm.chartType === 'horizontalBar') {
                      if (value.toString().length < 8) {
                        return vm.prefix + Number(value).toLocaleString('en-US')
                      }

                      return vm.prefix + Number(Math.round(value / 1000000)).toLocaleString('en-US') + 'mil'
                    }

                    return value
                  }
                }
              }],
              yAxes: [{
                ticks: {
                  beginAtZero: true,
                  callback: function (value, index, values) {
                    if (vm.chartType === 'bar') {
                      if (parseInt(value).toString().length < 8) {
                        return vm.prefix + Number(value).toLocaleString('en-US')
                      }

                      return vm.prefix + Number(Math.round(value / 1000000)).toLocaleString('en-US') + 'mil'
                    }

                    return value
                  }
                }
              }]
            },

            tooltips: {
              // http://www.chartjs.org/docs/#advanced-usage-external-tooltips
              // https://github.com/chartjs/Chart.js/issues/622#issuecomment-249752452
              // Look in chart.js/samples/tooltips
              callbacks: {
                label: function (tooltipItem, data) {
                  return vm.prefix + Number(vm.barChartAmounts[tooltipItem.index]).toLocaleString('en-US')
                }
              }
            },

            legend: {
              display: false
            }
          }
        })
      }
    }
  }
}
</script>

<style lang="scss">
@import "./src/sass/components/bar-chart"
</style>



// WEBPACK FOOTER //
// BarChart.vue?f0b6e232